import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { branding } from '../constants/branding';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { RootState } from '../store';
import { fetchSettings, updateSettings, clearSettingsSuccess, clearSettingsError, UserSettingsDto } from '../store/slices/userSettingsSlice';
import axiosInstance from '../services/axiosConfig';
import TwoFactorAuthManager from '../components/TwoFactorAuth/TwoFactorAuthManager';

const currencies = ['USD', 'EUR', 'GBP', 'JPY'];
const languages = ['English', 'Spanish', 'German', 'French'];
const accounts = ['Main Account', 'Savings', 'Crypto Wallet'];
const accentColors = [branding.primaryColor, branding.secondaryColor, branding.accentColor, '#8e44ad', '#f39c12', '#16a085'];

const Settings: React.FC = () => {
  const location = useLocation();

  // All hooks must be called unconditionally, before any return or if
  const dispatch = useAppDispatch();
  const { settings, isLoading, error, saveSuccess } = useAppSelector((state: RootState) => state.userSettings);
  const { profile } = useAppSelector(state => state.profile);
  const token = useAppSelector(state => state.auth.token);
  const navigate = useNavigate();

  // Individual state variables for controlled components
  const [twoFA, setTwoFA] = useState(true);
  const [biometric, setBiometric] = useState(false);
  const [loginAlerts, setLoginAlerts] = useState(true);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [smsAlerts, setSmsAlerts] = useState(false);
  const [pushAlerts, setPushAlerts] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [accentColor, setAccentColor] = useState(branding.primaryColor);
  const [fontSize, setFontSize] = useState(16);
  const [currency, setCurrency] = useState('USD');
  const [language, setLanguage] = useState('English');
  const [defaultAccount, setDefaultAccount] = useState('Main Account');
  const [devices, setDevices] = useState<any[]>([]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      setTwoFA(settings.security.twoFactorAuth);
      setBiometric(settings.security.biometricLogin);
      setLoginAlerts(settings.security.loginAlerts);
      setEmailAlerts(settings.notifications.emailAlerts);
      setSmsAlerts(settings.notifications.smsAlerts);
      setPushAlerts(settings.notifications.pushNotifications);
      setMarketingEmails(settings.notifications.marketingEmails);
      setDarkMode(settings.appearance.darkMode);
      setAccentColor(settings.appearance.accentColor);
      setFontSize(settings.appearance.fontSize);
      setCurrency(settings.bankingPreferences.defaultCurrency);
      setLanguage(settings.bankingPreferences.language);
      setDefaultAccount(settings.bankingPreferences.defaultAccount);
      setDevices(settings.devices);
    }
  }, [settings]);

  // Save settings to backend
  const saveSettings = async () => {
    if (!settings) return;
    const updatedSettings: UserSettingsDto = {
      account: settings.account,
      security: {
        twoFactorAuth: twoFA,
        biometricLogin: biometric,
        loginAlerts: loginAlerts,
      },
      notifications: {
        emailAlerts: emailAlerts,
        smsAlerts: smsAlerts,
        pushNotifications: pushAlerts,
        marketingEmails: marketingEmails,
      },
      appearance: {
        darkMode: darkMode,
        accentColor: accentColor,
        fontSize: fontSize,
      },
      privacy: settings.privacy,
      bankingPreferences: {
        defaultCurrency: currency,
        language: language,
        defaultAccount: defaultAccount,
      },
      devices: devices,
    };
    dispatch(updateSettings(updatedSettings));
  };

  // Update handlers with auto-save option
  const updateSetting = (setter: (value: any) => void, value: any) => {
    setter(value);
    setTimeout(saveSettings, 1000);
  };

  // Handlers
  const handleRevokeDevice = async (id: number) => {
    try {
      const res = await fetch(`/settings/devices/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to revoke device');
      setDevices(devices.filter(d => d.id !== id));
    } catch (err: any) {
      setTimeout(() => {
        dispatch(clearSettingsError());
      }, 5000);
    }
  };

  const handleDeleteDevice = async (id: string) => {
    try {
      const response = await axiosInstance.delete(`/settings/devices/${id}`);
      if (response.status === 200) {
        const data = response.data;
        if (data.success) {
          setDevices(devices.filter(device => device.id !== id));
        } else {
          console.error('Failed to delete device:', data.message);
        }
      }
    } catch (error) {
      console.error('Error deleting device:', error);
    }
  };

  const handleDeleteAccount = async () => {
    if (!token) {
      alert('Authentication required');
      return;
    }
    try {
      // ... existing code ...
    } catch (err: any) {
      setTimeout(() => {
        dispatch(clearSettingsError());
      }, 5000);
    }
  };

  // UI Components
  const Toggle = ({ value, onChange, label }: { value: boolean, onChange: (v: boolean) => void, label: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
      <span style={{ color: branding.textColor, fontSize: 16 }}>{label}</span>
      <button
        onClick={() => onChange(!value)}
        style={{
          width: 48,
          height: 24,
          background: value ? accentColor : '#ccc',
          borderRadius: 12,
          border: 'none',
          position: 'relative',
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        aria-label={label}
      >
        <div style={{
          width: 20,
          height: 20,
          background: '#fff',
          borderRadius: '50%',
          position: 'absolute',
          top: 2,
          left: value ? 26 : 2,
          transition: 'left 0.2s',
        }} />
      </button>
    </div>
  );

  const SectionCard = ({ icon, title, children, style }: { icon: string, title: string, children: React.ReactNode, style?: React.CSSProperties }) => (
    <div style={{
      background: branding.cardBackground,
      borderRadius: 20,
      boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      padding: 32,
      marginBottom: 32,
      maxWidth: 700,
      marginLeft: 'auto',
      marginRight: 'auto',
      ...style,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <span style={{ fontSize: 28 }}>{icon}</span>
        <h2 style={{ margin: 0, color: branding.textColor, fontWeight: 700, fontSize: 22 }}>{title}</h2>
      </div>
      {children}
      </div>
    );

  if (isLoading) return <div>Loading settings...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: branding.fontFamily,
      padding: 0,
      margin: 0,
      paddingTop: 32,
    }}>
      {/* Page Title */}
      <div style={{
        maxWidth: 1200,
        margin: '0 auto 32px auto',
        padding: '0 16px',
      }}>
        <h1 style={{
          color: branding.textColor,
          fontWeight: 700,
          fontSize: 28,
          margin: 0,
          marginBottom: 8,
        }}>Settings</h1>
        <p style={{
          color: '#666',
          fontSize: 16,
          margin: 0,
        }}>Manage your account preferences, security, notifications, and more</p>
      </div>

      {/* Success/Error Messages */}
      {saveSuccess && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          background: '#4CAF50',
          color: 'white',
          padding: '12px 24px',
          borderRadius: 8,
          zIndex: 1001,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          Settings saved successfully!
        </div>
      )}
      {error && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          background: '#f44336',
          color: 'white',
          padding: '12px 24px',
          borderRadius: 8,
          zIndex: 1001,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {error}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 24,
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 16px',
        }}
      >
        <SectionCard icon="👤" title="Account" style={{ minHeight: 220, width: 340, margin: 12 }}>
          <button onClick={() => setShowEditProfile(true)} style={buttonStyle}>Edit Profile</button>
          <button onClick={() => setShowChangePassword(true)} style={buttonStyle}>Change Password</button>
        </SectionCard>
        <SectionCard icon="🔒" title="Security" style={{ width: '100%', maxWidth: 700, margin: 12 }}>
          <div style={{ marginBottom: 24 }}>
            <TwoFactorAuthManager onStatusChange={(enabled) => setTwoFA(enabled)} />
          </div>
          <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #eee' }}>
            <Toggle value={biometric} onChange={(value) => updateSetting(setBiometric, value)} label="Biometric Login" />
            <Toggle value={loginAlerts} onChange={(value) => updateSetting(setLoginAlerts, value)} label="Login Alerts" />
          </div>
        </SectionCard>
        <SectionCard icon="✉️" title="Notifications" style={{ minHeight: 220, width: 340, margin: 12 }}>
          <Toggle value={emailAlerts} onChange={(value) => updateSetting(setEmailAlerts, value)} label="Email Alerts" />
          <Toggle value={smsAlerts} onChange={(value) => updateSetting(setSmsAlerts, value)} label="SMS Alerts" />
          <Toggle value={pushAlerts} onChange={(value) => updateSetting(setPushAlerts, value)} label="Push Notifications" />
          <Toggle value={marketingEmails} onChange={(value) => updateSetting(setMarketingEmails, value)} label="Marketing Emails" />
        </SectionCard>
        <SectionCard icon="🎨" title="Appearance" style={{ minHeight: 220, width: 340, margin: 12 }}>
          <Toggle value={darkMode} onChange={(value) => updateSetting(setDarkMode, value)} label="Dark Mode" />
          <div style={{ margin: '12px 0' }}>
            <div style={{ color: branding.textColor, fontSize: 15, marginBottom: 6 }}>Accent Color</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {accentColors.map(color => (
                <button
                  key={color}
                  onClick={() => updateSetting(setAccentColor, color)}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: color === accentColor ? `2px solid ${branding.primaryColor}` : '1px solid #eee',
                    background: color,
                    cursor: 'pointer',
                    outline: 'none',
                    boxShadow: color === accentColor ? '0 0 0 2px #e3f0ff' : 'none',
                    transition: 'border 0.2s, box-shadow 0.2s',
                  }}
                  aria-label={`Accent color ${color}`}
                />
              ))}
            </div>
          </div>
          <div style={{ margin: '12px 0' }}>
            <div style={{ color: branding.textColor, fontSize: 15, marginBottom: 6 }}>Font Size</div>
            <input
              type="range"
              min={14}
              max={22}
              value={fontSize}
              onChange={e => updateSetting(setFontSize, Number(e.target.value))}
              style={{ width: 120 }}
            />
            <span style={{ marginLeft: 8, color: branding.textColor, fontWeight: 600 }}>{fontSize}px</span>
          </div>
        </SectionCard>
        <SectionCard icon="🔏" title="Privacy & Data" style={{ minHeight: 220, width: 340, margin: 12 }}>
          <button onClick={() => alert('Data download started!')} style={buttonStyle}>Download My Data</button>
          <button onClick={() => setShowDeleteAccount(true)} style={{ ...buttonStyle, background: '#fff', color: branding.errorColor, border: `1.5px solid ${branding.errorColor}` }}>Delete Account</button>
          <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
            <a href="/privacy" style={{ color: branding.primaryColor, fontSize: 14, textDecoration: 'underline' }}>Privacy Policy</a>
            <a href="/terms" style={{ color: branding.primaryColor, fontSize: 14, textDecoration: 'underline' }}>Terms of Service</a>
          </div>
        </SectionCard>
        <SectionCard icon="💱" title="Banking Preferences" style={{ minHeight: 220, width: 340, margin: 12 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: branding.textColor, fontSize: 15, marginBottom: 6 }}>Default Currency</div>
            <select value={currency} onChange={e => updateSetting(setCurrency, e.target.value)} style={selectStyle}>
              {currencies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: branding.textColor, fontSize: 15, marginBottom: 6 }}>Language</div>
            <select value={language} onChange={e => updateSetting(setLanguage, e.target.value)} style={selectStyle}>
              {languages.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
    <div>
            <div style={{ color: branding.textColor, fontSize: 15, marginBottom: 6 }}>Default Account</div>
            <select value={defaultAccount} onChange={e => updateSetting(setDefaultAccount, e.target.value)} style={selectStyle}>
              {accounts.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </SectionCard>
      </div>
      {/* Manual Save Button */}
      <div style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1000,
      }}>
        <button 
          onClick={saveSettings}
          disabled={isLoading}
          style={{
            ...buttonStyle,
            background: isLoading ? '#ccc' : branding.primaryColor,
            padding: '16px 32px',
            borderRadius: 50,
            boxShadow: '0 8px 24px rgba(10,132,255,0.2)',
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          {isLoading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
      {/* Modals */}
      {showEditProfile && (
        <Modal title="Edit Profile" onClose={() => setShowEditProfile(false)}>
          <div style={{ color: branding.textColor, fontSize: 16 }}>Profile editing form goes here.</div>
        </Modal>
      )}
      {showChangePassword && (
        <Modal title="Change Password" onClose={() => setShowChangePassword(false)}>
          <div style={{ color: branding.textColor, fontSize: 16 }}>Password change form goes here.</div>
        </Modal>
      )}
      {showDeleteAccount && (
        <Modal title="Delete Account" onClose={() => setShowDeleteAccount(false)}>
          <div style={{ color: branding.textColor, fontSize: 16, marginBottom: 18 }}>Are you sure you want to delete your account? This action cannot be undone.</div>
          <button onClick={() => { setShowDeleteAccount(false); alert('Account deleted!'); }} style={{ ...buttonStyle, background: branding.errorColor, color: '#fff' }}>Delete</button>
        </Modal>
      )}
    </div>
  );
};

const buttonStyle: React.CSSProperties = {
  padding: '12px 24px',
  background: branding.primaryColor,
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
  marginRight: 18,
  marginBottom: 12,
  boxShadow: '0 4px 16px rgba(10,132,255,0.08)',
  transition: 'all 0.2s',
};

const selectStyle: React.CSSProperties = {
  width: 180,
  padding: '10px 16px',
  borderRadius: 10,
  border: '1.5px solid #eee',
  fontSize: 15,
  color: branding.textColor,
  background: '#f7fafd',
  fontWeight: 500,
};

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px',
  }}>
    <div style={{
      background: '#fff',
      borderRadius: 24,
      padding: 40,
      maxWidth: 480,
      width: '100%',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      textAlign: 'center',
    }}>
      <h3 style={{
        margin: 0,
        marginBottom: 24,
        color: branding.textColor,
        fontSize: 24,
        fontWeight: 700,
      }}>{title}</h3>
      {children}
      <button onClick={onClose} style={{ ...buttonStyle, background: '#fff', color: branding.primaryColor, border: `1.5px solid ${branding.primaryColor}`, marginTop: 18 }}>Close</button>
    </div>
  </div>
);

export default Settings; 